// Sound effects for Tetris game using Web Audio API
// Simulates classic Tetris sounds

class SoundEffects {
  constructor() {
    this.audioContext = null;
    this.enabled = true;
    this.volume = 0.3;
    this.pendingSequences = [];
    this.initAudioContext();
  }

  initAudioContext() {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (AudioContext) {
        this.audioContext = new AudioContext();
      }
    } catch (e) {
      console.warn('Web Audio API not supported:', e);
      this.enabled = false;
    }
  }

  // Resume audio context if it's suspended (browser autoplay policy)
  async resume() {
    if (!this.enabled) return;
    if (!this.audioContext) {
      this.initAudioContext();
    }
    if (this.audioContext && this.audioContext.state === 'suspended') {
      try {
        await this.audioContext.resume();
      } catch (e) {
        console.warn('Error resuming AudioContext:', e);
      }
    }
  }

  // Suspend audio context (useful when app is in background)
  async suspend() {
    if (this.audioContext && this.audioContext.state === 'running') {
      try {
        await this.audioContext.suspend();
      } catch (e) {
        console.warn('Error suspending AudioContext:', e);
      }
    }
  }

  // Play a frequency for a duration
  async playTone(frequency, duration, type = 'sine') {
    if (!this.enabled) return;

    if (!this.audioContext || this.audioContext.state === 'closed') {
      this.initAudioContext();
    }

    await this.resume();

    if (!this.audioContext) return;

    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(this.audioContext.destination);

    oscillator.type = type;
    oscillator.frequency.value = frequency;

    gainNode.gain.value = this.volume;
    gainNode.gain.exponentialRampToValueAtTime(
      0.01,
      this.audioContext.currentTime + duration
    );

    oscillator.start(this.audioContext.currentTime);
    oscillator.stop(this.audioContext.currentTime + duration);
  }

  // Play multiple tones in sequence
  async playSequence(notes, tempo = 0.1) {
    if (!this.enabled) return;

    await this.resume();

    notes.forEach((note, index) => {
      const timeoutId = setTimeout(() => {
        this.playTone(note.frequency, note.duration || tempo, note.type || 'sine');
        // Remove from pending when done
        this.pendingSequences = this.pendingSequences.filter(id => id !== timeoutId);
      }, index * tempo * 1000);
      this.pendingSequences.push(timeoutId);
    });
  }

  // Move sound - short beep
  move() {
    this.playTone(400, 0.05, 'square');
  }

  // Rotate sound - higher pitched beep
  rotate() {
    this.playTone(600, 0.05, 'square');
  }

  // Lock piece sound - lower pitched
  lock() {
    this.playTone(200, 0.1, 'square');
  }

  // Line clear sound - ascending tones
  lineClear(lines = 1) {
    const sequences = {
      1: [
        { frequency: 659, duration: 0.1 },
        { frequency: 784, duration: 0.15 }
      ],
      2: [
        { frequency: 659, duration: 0.08 },
        { frequency: 784, duration: 0.08 },
        { frequency: 880, duration: 0.15 }
      ],
      3: [
        { frequency: 659, duration: 0.07 },
        { frequency: 784, duration: 0.07 },
        { frequency: 880, duration: 0.07 },
        { frequency: 1047, duration: 0.15 }
      ],
      4: [ // Tetris!
        { frequency: 659, duration: 0.06 },
        { frequency: 784, duration: 0.06 },
        { frequency: 880, duration: 0.06 },
        { frequency: 1047, duration: 0.06 },
        { frequency: 1175, duration: 0.2 }
      ]
    };

    const sequence = sequences[Math.min(lines, 4)] || sequences[1];
    this.playSequence(sequence, 0.08);
  }

  // Hard drop sound - falling tone
  hardDrop() {
    if (!this.enabled || !this.audioContext) return;

    this.resume();

    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(this.audioContext.destination);

    oscillator.type = 'sawtooth';
    oscillator.frequency.value = 800;

    // Descending frequency
    oscillator.frequency.exponentialRampToValueAtTime(
      100,
      this.audioContext.currentTime + 0.2
    );

    gainNode.gain.value = this.volume;
    gainNode.gain.exponentialRampToValueAtTime(
      0.01,
      this.audioContext.currentTime + 0.2
    );

    oscillator.start(this.audioContext.currentTime);
    oscillator.stop(this.audioContext.currentTime + 0.2);
  }

  // Game over sound - descending tones
  gameOver() {
    const sequence = [
      { frequency: 440, duration: 0.2 },
      { frequency: 415, duration: 0.2 },
      { frequency: 392, duration: 0.2 },
      { frequency: 370, duration: 0.3 }
    ];
    this.playSequence(sequence, 0.15);
  }

  // Level up sound - ascending rapid tones
  levelUp() {
    const sequence = [
      { frequency: 523, duration: 0.1 },
      { frequency: 659, duration: 0.1 },
      { frequency: 784, duration: 0.1 },
      { frequency: 1047, duration: 0.2 }
    ];
    this.playSequence(sequence, 0.08);
  }

  // Set volume (0 to 1)
  setVolume(volume) {
    this.volume = Math.max(0, Math.min(1, volume));
  }

  // Enable/disable sounds
  setEnabled(enabled) {
    this.enabled = enabled;
  }

  // Stop all sounds and clean up
  stopAll() {
    // Clear pending sequences
    this.pendingSequences.forEach(id => clearTimeout(id));
    this.pendingSequences = [];

    if (this.audioContext) {
      if (this.audioContext.state !== 'closed') {
        this.audioContext.close().catch(e => console.error('Error closing AudioContext:', e));
      }
      this.audioContext = null;
    }
  }
}

// Create singleton instance
const soundEffects = new SoundEffects();

export default soundEffects;
